import type { PendingFiltersDraft, PendingFilters } from "../services/api/domains/pending/pending.types";

/**
 * Mapping of draft filter keys to API filter keys
 */
const FILTER_KEY_MAP: Record<keyof PendingFiltersDraft, keyof PendingFilters | null> = {
  stat: "stat",
  locationIds: "locations",
  instrumentIds: "instruments",
  sampleTypeIds: "sampleTypes",
  visitTypeIds: "visitTypes",
  requesterIds: "requesters",
  doctorIds: "doctors",
  maxRows: "maxRows",
};

/**
 * Check if filters are active (non-default values)
 */
export const hasActiveFilters = (filters: PendingFiltersDraft): boolean => {
  return Object.entries(filters).some(([key, value]) => {
    // Skip stat if it's false (default)
    if (key === "stat") return value === true;

    // Check if array has items
    if (Array.isArray(value)) return value.length > 0;

    // Check if value exists and is not default
    return value !== undefined && value !== null && value !== 0;
  });
};

/**
 * Convert draft filters to API format
 * - Removes falsy/empty values
 * - Converts arrays to comma-separated strings
 * - Maps draft keys to API keys
 */
export const convertFiltersToApiFormat = (draft: PendingFiltersDraft): PendingFilters => {
  const api: Record<string, any> = {};

  for (const [draftKey, value] of Object.entries(draft)) {
    const apiKey = FILTER_KEY_MAP[draftKey as keyof PendingFiltersDraft];

    if (!apiKey || value === null || value === undefined) continue;

    if (typeof value === "boolean") {
      if (value) api[apiKey] = true;
    } else if (Array.isArray(value)) {
      if (value.length > 0) api[apiKey] = value.join(",");
    } else if (typeof value === "number") {
      if (value !== 0) api[apiKey] = value;
    } else if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) api[apiKey] = trimmed;
    } else {
      api[apiKey] = value;
    }
  }

  return api as PendingFilters;
};
